import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { createContext, useContext, useId, type ReactElement, type ReactNode } from 'react';

/** Widest column count a definition list starts at. */
export type DefinitionColumns = 2 | 3 | 4 | 5;

/**
 * How a `<dt>` is styled.
 *
 * - `'caps'` (default): micro-caps — uppercase, 700, 0.06em tracking. This is
 *   what every *current* consumer of this component renders, byte-identically
 *   — it has no production consumer today (`ClientHeader` and
 *   `OpportunityDetailPage` draw their own near-identical header facts grid
 *   with a hand-rolled `<Box component="dl">`, not this component), so the
 *   pin below guards a future migration, not a live screen.
 * - `'title'`: Title Case `text.secondary` caption, no transform. The in-card
 *   idiom the Client Success design uses for its Governance, Success Plan and
 *   QBR lists ("Health", "CS Lifecycle", "Relationship Goal" — Figma
 *   `276:3929`), where the header's shouting caps would read as a second
 *   header stacked under the first (QFLY-484). Every current consumer uses
 *   this variant.
 */
export type DefinitionTermVariant = 'caps' | 'title';

/**
 * Per-track `fr` weights at the WIDEST reflow step only — `columns` still
 * selects which `REFLOW` entry (and its `@container` steps) applies; `tracks`
 * only reshapes that widest step's `gridTemplateColumns` from equal `1fr`s to
 * the given ratios. Length is tied to `columns` at the type level (a 5-column
 * list cannot be given 3 weights), so a mismatch is a compile error, not a
 * `console.error` a dev console might never show.
 *
 * ## Deriving weights from a Figma frame
 *
 * Figma draws tracks with NO column gap — a track's own width already
 * includes the space up to the next one. This grid keeps a real 24px
 * `columnGap` between tracks, so reproducing Figma's column START positions
 * means giving every track EXCEPT THE LAST its Figma width minus 24, and
 * giving the last track its Figma width unchanged (nothing trails it). e.g.
 * Figma `[221.5, 221.5, 320, 221.5, 221.5]` (sums to 1206, starts at
 * `0/221.5/443/763/984.5`) becomes `tracks={[197.5, 197.5, 296, 197.5,
 * 221.5]}` — starts land at the same four positions with a real 24px gap
 * between. There is deliberately no separate "gutter" prop for this: baking
 * the arithmetic into the weights themselves, once, here, is simpler and
 * safer than a second prop that would also have to special-case a
 * full-row-spanning `DefinitionItem` (whose box must still reach the list's
 * own right edge) and the narrower `@container` steps (which don't use
 * `tracks` at all — see below).
 *
 * ## Why each track is `minmax(150px, Nfr)`, not `minmax(0, Nfr)`
 *
 * `0` as the minimum is what the un-weighted equal-`1fr` steps already use,
 * and it is also how the REFLOW thresholds (860/700/520/350) were derived —
 * on an UNWEIGHTED split, every track is the same size, so "the smallest
 * track" and "the widest step's threshold" are one and the same number.
 * Weighted tracks break that equivalence: at a container width the step
 * doesn't retreat from, the NARROWEST weighted track can fall under the
 * 150px readable floor requirements §4.6 F8 (and this engagement's `e2e`
 * suite, `client-success-tab.spec.ts`) require — even though the step's
 * *average* track is still wide enough. `minmax(150px, Nfr)` fixes this at
 * the algorithm, not by re-deriving a new threshold per consumer: CSS Grid
 * satisfies every track's 150px minimum FIRST, then distributes only the
 * space left over in proportion to the `fr` weights. A track can therefore
 * never render under 150px while its step is showing; the existing
 * thresholds need no change, because `n × 150 + (n-1) × 24` is already
 * comfortably under each one (5-col: 846 < 860; 2-col: 324 < 350).
 *
 * That math only proves the floors don't force an early reflow step-down —
 * it does NOT mean a single low-weight track stays proportional all the way
 * down to that threshold. A track's un-clamped share is `(weight / sum of
 * weights) × (container − gaps)`, so the SMALLEST-weight track can hit its
 * own 150px floor at a container width well above the reflow threshold
 * (confirmed on `SuccessHealthGovernanceCard`'s 5 weights, CST-46: the 0.8
 * track clamps to exactly 150px once the card narrows under ~1184px, far
 * above the 860px step-down). Once that happens the clamped track no longer
 * scales with its declared weight — CSS satisfies its floor first, then
 * redistributes only the leftover space among the rest. A consumer whose
 * `e2e` coverage asserts proportionality (as this one's does) must exclude
 * a floored track from that check, not assume every weighted consumer stays
 * proportional across its whole widest step.
 *
 * ## Scope
 *
 * `CST-42` added the capability only — no existing consumer was given
 * `tracks` in that story. `CST-46` is the first consumer, passing 5 weights
 * on `SuccessHealthGovernanceCard`'s own `<DefinitionList>` call and updating
 * that card's `e2e` "equal tracks" assertion to a weighted one with a 1:1
 * note. `SuccessPlanReadView` / `QbrLogPanel` remain on equal tracks until
 * their own section stories (`CST-49`..`CST-51`, `CST-53`) are ready to pass
 * `tracks`, per the Figma widths in
 * `docs/plans/client-success-dashboard-2026-09/` (via the dashboard
 * engagement) and this engagement's own design pack.
 */
type TrackWeights<C extends DefinitionColumns> = C extends 2
  ? readonly [number, number]
  : C extends 3
    ? readonly [number, number, number]
    : C extends 4
      ? readonly [number, number, number, number]
      : readonly [number, number, number, number, number];

export interface DefinitionListProps<C extends DefinitionColumns = DefinitionColumns> {
  /**
   * Column count at the card's widest. The list steps down from here as the
   * CARD narrows — see the reflow note below.
   */
  readonly columns: C;
  /** See the `TrackWeights` doc above. Omit for the original equal-`1fr`
   * layout (every existing consumer). */
  readonly tracks?: TrackWeights<C>;
  /** `DefinitionItem`s. Children, not an `items[]` array — see the note below. */
  readonly children: ReactNode;
  readonly sx?: SxProps<Theme>;
  /**
   * Term styling for every `DefinitionItem` in this list. Set once on the list,
   * not per item, so a card cannot mix the two by accident. Defaults to `'caps'`.
   */
  readonly termVariant?: DefinitionTermVariant;
}

const TermVariantContext = createContext<DefinitionTermVariant>('caps');

/**
 * Reflow steps, by container width.
 *
 * Deliberate, not defaulted. These are keyed to the CARD's width via container
 * queries, not the viewport, because the nav drawer (220px expanded / 64px
 * collapsed) makes viewport width overstate the space a card actually has —
 * the cause of a real production layout collision documented in
 * `ClientHeader.tsx`. The card declares the container (`SectionCard
 * asContainer`); this list only reads it.
 *
 * The step-down is content-driven rather than a mechanical halving. A five-cell
 * governance row reads as three related pairs plus a status, so 5 → 3 → 2 → 1
 * keeps associated cells adjacent at every step; 5 → 4 → 2 would split them.
 *
 * Thresholds are derived from a ~150px minimum readable cell plus a 24px gutter,
 * the same way `ClientHeader.tsx` derives its 640: 3 cols needs ~520, 2 needs
 * ~350. Below that, one column. `tracks` (above) reshapes only the WIDEST
 * step's `gridTemplateColumns`; every `@container` step below it is always
 * the plain equal-share layout defined here, unchanged.
 */
const REFLOW: Readonly<Record<DefinitionColumns, SxProps<Theme>>> = {
  5: {
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    '@container (max-width: 860px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    '@container (max-width: 520px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    '@container (max-width: 350px)': { gridTemplateColumns: '1fr' },
  },
  4: {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    '@container (max-width: 700px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    '@container (max-width: 350px)': { gridTemplateColumns: '1fr' },
  },
  3: {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    '@container (max-width: 520px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    '@container (max-width: 350px)': { gridTemplateColumns: '1fr' },
  },
  2: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    '@container (max-width: 350px)': { gridTemplateColumns: '1fr' },
  },
};

/**
 * Builds the widest-step `gridTemplateColumns` for `columns`, reshaped by
 * `tracks` when given.
 *
 * `{ ...REFLOW[columns], gridTemplateColumns: tpl }` — NOT
 * `{ gridTemplateColumns: tpl, ...REFLOW[columns] }` — because `
 * gridTemplateColumns` already exists as the FIRST key in `REFLOW[columns]`;
 * overwriting an existing key's value leaves it at its original position in
 * JS's object key order, so the base rule still precedes the `@container`
 * keys in the object (and, downstream, in the emitted stylesheet — stylis
 * preserves source/key order). Writing the base key last would instead
 * re-add it at the END, after every `@container` block, where it would beat
 * all of them regardless of container width — the exact trap the design's
 * resolved decision 7 names.
 *
 * Invalid weights (non-finite, zero or negative — each produces either an
 * invalid `grid-template-columns` the browser silently drops, or a nonsense
 * layout) fall back to the unweighted step rather than ship either. The
 * check is dev-only: Vite's `import.meta.env.DEV` is replaced with a
 * constant at build time, so the branch (and the `console.error` inside it)
 * is dead code in production.
 */
const reflowFor = <C extends DefinitionColumns>(columns: C, tracks?: TrackWeights<C>): SxProps<Theme> => {
  if (!tracks) return REFLOW[columns];
  const valid = tracks.length === columns && tracks.every((w) => Number.isFinite(w) && w > 0);
  if (!valid) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- dev-only diagnostic, numeric values only, stripped from prod.
      console.error('DefinitionList: tracks must have exactly `columns` finite, positive weights.', {
        columns,
        tracks,
      });
    }
    return REFLOW[columns];
  }
  return {
    ...REFLOW[columns],
    gridTemplateColumns: tracks.map((w) => `minmax(150px, ${w}fr)`).join(' '),
  };
};

/**
 * A responsive `<dl>` for label/value metadata.
 *
 * Extracted from the byte-for-byte duplicate at `ClientHeader.tsx` and
 * `OpportunityDetailPage.tsx` — the only one of the repo's three label/value
 * idioms with real `<dt>`/`<dd>` semantics AND container queries. The other two
 * (a MUI `Grid` with no `dl` semantics, and a bare `Stack`) are not extracted
 * here; converting their call sites — including the two above, which still
 * hand-roll their own `<dl>` rather than import this component — is separate
 * work.
 *
 * Takes **children**, not an `items[]` array. That is required, not preferred:
 * a component can contribute several cells from a single hook (the retired
 * `ClientCsSummaryStats` returned three), and an array prop cannot express "one
 * component contributes three cells" without hoisting that hook into the parent
 * — which would re-render the whole list on every one of its updates.
 *
 * A plain generic function, not the repo's usual `const X = (props) => (...)`:
 * TSX's arrow-generic syntax (`<C,>(...)`) is easy to mistype as a stray JSX
 * tag, so a `function` declaration sidesteps the ambiguity entirely.
 */
export function DefinitionList<C extends DefinitionColumns = DefinitionColumns>({
  columns,
  tracks,
  children,
  sx,
  termVariant = 'caps',
}: DefinitionListProps<C>): ReactElement {
  return (
    <TermVariantContext.Provider value={termVariant}>
      <Box
        component="dl"
        sx={[
          {
            m: 0,
            display: 'grid',
            columnGap: 3,
            rowGap: 2,
            minWidth: 0,
          },
          reflowFor(columns, tracks),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {children}
      </Box>
    </TermVariantContext.Provider>
  );
}

/**
 * Colour now lives HERE, not as a hardcoded prop on `DefinitionItem`'s `<dt>`
 * Typography (CST-46) — `'title'` needs a different ink (`grey.500`, Figma
 * `276:4044`) than `'caps'`'s original `text.secondary`, and a per-variant
 * prop would have meant branching inside `DefinitionItem` itself. Every
 * `'title'` consumer (`SuccessHealthGovernanceCard`, `SuccessPlanReadView`,
 * `QbrLogPanel` — this is a SHARED primitive, not scoped to one card) picks
 * up the new 13px/400/grey.500 term styling together, which is the intended
 * Figma-fidelity ripple, not an accidental one.
 */
const TERM_SX: Readonly<Record<DefinitionTermVariant, SxProps<Theme>>> = {
  caps: {
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 700,
    color: 'text.secondary',
  },
  title: { fontWeight: 400, color: 'grey.500', fontSize: '13px' },
};

/** `<dd>` top margin by term variant. `'title'` is 2px — measured from the
 * Figma render (term and value line-box tops, not the frame's own "pad-top
 * 8" figure, which includes the value's own line-height inset) — `'caps'`
 * keeps its original 4px (`ClientHeader`'s own spacing, byte-identical). */
const DD_MARGIN_TOP: Readonly<Record<DefinitionTermVariant, { readonly mt: string | number }>> = {
  caps: { mt: 0.5 },
  title: { mt: '2px' },
};

export interface DefinitionItemProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly sx?: SxProps<Theme>;
}

/**
 * One `<dt>`/`<dd>` pair.
 *
 * The two are associated with a `useId`-based `aria-labelledby` so the value is
 * queryable by its label: `getByRole('definition', { name: 'CS Lifecycle' })`.
 *
 * That pairing is necessary, not decorative. A `<dt>`'s own accessible name is
 * empty — the `term` role takes no name from content — so
 * `getByRole('term', { name })` can never resolve, and a test written that way
 * silently matches nothing. Without the association, every cell assertion in
 * Phase 2 would have to fall back to positional indexing.
 */
export const DefinitionItem = ({ label, children, sx }: DefinitionItemProps): ReactElement => {
  const labelId = useId();
  const termVariant = useContext(TermVariantContext);
  return (
    <Box sx={[{ minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Typography
        component="dt"
        id={labelId}
        variant="caption"
        // Colour comes from `TERM_SX[termVariant]` now, not a hardcoded
        // prop here — see that constant's own comment.
        sx={TERM_SX[termVariant]}
      >
        {label}
      </Typography>
      <Box
        component="dd"
        aria-labelledby={labelId}
        sx={[{ m: 0, overflowWrap: 'anywhere' }, DD_MARGIN_TOP[termVariant]]}
      >
        {children}
      </Box>
    </Box>
  );
};
